import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";

const SECTIONS = [
  {
    key: "regulatory-status",
    title: "Regulatory Status",
    body: [
      "Kellermann Reid & Co. is a private markets platform in strategic formation. The platform is not currently a registered investment adviser, regulated investment manager or authorised securities firm in any jurisdiction.",
      "Vehicle formation, fund authorisation and any related regulatory registrations are in progress. Subscription, marketing and on-boarding of investors will only be conducted in compliance with the applicable rules of each jurisdiction once vehicles are formed and authorised.",
      "Nothing on this website constitutes an offer to sell or a solicitation of an offer to buy any security, fund interest or other financial instrument, nor an offer of advisory services.",
    ],
  },
  {
    key: "cayman-platform",
    title: "Cayman Islands Fund Platform",
    body: [
      "Where engagement is finalised, the platform's intended fund infrastructure is to be provided on the CV5 SPC platform operated by CV5 Capital, a Cayman Islands–registered fund platform provider.",
      "Cayman governance, registered office services and corporate-secretarial coordination are intended to be supported by Bell Rock Group, subject to finalisation of engagement terms.",
      "These engagements are described on the platform on a working-title basis and remain in formation.",
    ],
  },
  {
    key: "audience",
    title: "Permitted Audience",
    body: [
      "Materials on this website are intended for sophisticated readers — professional, qualified, institutional and accredited investors and their advisers — and are not directed at retail investors or members of the general public.",
      "Access to and use of this website does not establish a client, advisory or fiduciary relationship of any kind.",
      "Where local rules require specific investor eligibility or pre-qualification, such requirements will apply at the time of any offer.",
    ],
  },
  {
    key: "correspondence",
    title: "Jurisdictional Correspondence & Marketing Restrictions",
    body: [
      "Kellermann Reid & Co. does not solicit investors in jurisdictions where such solicitation is restricted, unauthorised or otherwise impermissible. Access to materials on this website is made available only where, and only to the extent, lawful under the applicable rules of the reader's home jurisdiction.",
      "In certain jurisdictions, enquiries may be directed to local correspondents acting on behalf of the partnership solely for the purposes of selective engagement on a documented basis. Such correspondents do not constitute placement agents, marketing distributors or authorised representatives unless specifically engaged in writing and disclosed in the offering documentation of the relevant vehicle.",
      "European Union & United Kingdom — Engagement is conducted on a reverse-solicitation basis or, where required, through arrangements compliant with the Alternative Investment Fund Managers Directive (AIFMD) and applicable national private-placement regimes. Materials are directed solely at professional clients and eligible counterparties within the meaning of MiFID II and equivalent UK rules.",
      "Switzerland — Materials are directed solely at professional and institutional clients within the meaning of the Swiss Financial Services Act (FinSA). The platform does not market to retail clients in or from Switzerland and will, where required, appoint a duly authorised Swiss representative and paying agent before any offering to non-qualified investors.",
      "United States — Any offer or sale of fund interests will be conducted in reliance on available exemptions under the Securities Act of 1933 and the Investment Company Act of 1940 (including Regulation D). This website does not constitute an offer in the United States or to U.S. persons within the meaning of Regulation S.",
      "United Arab Emirates — Engagement is directed solely at professional investors as defined under the applicable federal and free-zone rules, including those of the Securities and Commodities Authority (SCA), the Dubai Financial Services Authority (DFSA) within the DIFC, and the Financial Services Regulatory Authority (FSRA) within the ADGM. No marketing or solicitation is conducted on a public-offer basis. Materials are made available only on a private, by-introduction basis.",
      "Asia-Pacific — The partnership engages with investors through its Asia-Pacific Partner on a documented, by-introduction basis, including in Singapore (under the exempt accredited- and institutional-investor regime of the Monetary Authority of Singapore), Hong Kong (to professional investors under the Securities and Futures Ordinance), Japan (under available exemptions of the Financial Instruments and Exchange Act) and other regional markets. No marketing or distribution activity is conducted in any jurisdiction where local licensing is required and has not been obtained.",
      "Other Jurisdictions — The platform's investor base is global. In any jurisdiction not specifically named above, materials are made available only where lawful, and only to persons whose receipt and use is permitted under applicable local law. The standard is identical across markets: documented engagement, eligibility-led, by introduction.",
      "Investors are responsible for confirming their eligibility to receive materials and to invest under the applicable law of their home jurisdiction. Where in doubt, readers should consult their own legal, tax and regulatory advisers before proceeding.",
    ],
  },
  {
    key: "forward-looking",
    title: "Forward-Looking Statements",
    body: [
      "Statements about vehicle architecture, target capacity, intended timing of fund formation, target sectors and intended counterparties are forward-looking and subject to documentation, regulatory authorisation, market conditions and capital commitments.",
      "Working-title vehicle names, target sizes and indicative fees are illustrative only and may change. No assurance is given that the partnership will achieve any particular outcome, capacity or timing.",
    ],
  },
  {
    key: "no-advice",
    title: "No Investment, Legal or Tax Advice",
    body: [
      "The content of this website — including research papers, perspectives and platform descriptions — is for informational purposes only. It is not, and should not be construed as, investment, legal, tax, accounting or other professional advice.",
      "Readers must make their own assessment of the matters discussed and obtain independent professional advice as appropriate.",
    ],
  },
  {
    key: "third-party",
    title: "Third-Party References",
    body: [
      "References on this website to third-party firms — including CV5 Capital, Bell Rock Group and the partnership's intended administration, custody and audit counterparties — describe the platform's working engagement framework. They do not constitute endorsements by, or representations of, those third parties.",
      "Logos and trademarks are the property of their respective owners.",
    ],
  },
  {
    key: "capital-coordination",
    title: "Capital Coordination & Selective Pathways",
    body: [
      "Kellermann Reid & Co. is mandate-led. Opportunities that meet platform criteria may be considered for direct platform capital formation, investor access, or fund-structured solutions. Where a transaction sits outside our direct mandate parameters, we may, where appropriate, coordinate introductions to selected equity, private credit, real estate debt, or strategic capital partners within our external network.",
      "Any such engagement remains subject to diligence, suitability, jurisdictional requirements, and final approval by the relevant capital provider.",
      "Kellermann Reid & Co. does not act as a placement agent or broker-dealer in any jurisdiction. The partnership does not guarantee funding, transaction outcomes, or the performance of any third-party capital provider to whom an introduction may be made. Introductions are made on a documented, confidential basis and do not constitute an offer or solicitation.",
    ],
  },
  {
    key: "complaints",
    title: "Complaints & Enquiries",
    body: [
      "Complaints or formal enquiries from regulators, counterparties or counsel should be directed to investors@krprivatecapital.com and will be acknowledged on a documented basis.",
      "Where appropriate, complaints will be escalated through the partnership's independent risk, legal and compliance function.",
    ],
  },
];

const DisclosuresPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.disclosuresPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Disclosures"
          label="Regulatory Posture · Working Engagements · Forward-Looking Statements"
          title={
            <>
              Documented{" "}
              <span className="italic text-[var(--kr-gold)]">disclosures</span>.
            </>
          }
          description="A consolidated statement of the platform's regulatory posture, working engagements with Cayman service providers, and the forward-looking nature of vehicle-level descriptions. To be read alongside the Legal Notices, Privacy Notice and Cookies Notice."
          breadcrumb="Disclosures"
        />

        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-32 space-y-3">
                <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-2">
                  On this page
                </div>
                {SECTIONS.map((s, i) => (
                  <a
                    key={s.key}
                    href={`#${s.key}`}
                    className="block text-[13px] text-[var(--kr-text-muted)] hover:text-[var(--kr-gold)] transition-colors py-1 border-b border-[var(--kr-line-soft)]"
                  >
                    {String(i + 1).padStart(2, "0")} — {s.title}
                  </a>
                ))}
              </div>
            </aside>

            <div className="lg:col-span-9 lg:pl-8 space-y-16">
              {SECTIONS.map((s, i) => (
                <section
                  key={s.key}
                  id={s.key}
                  data-testid={`${LANDING.disclosuresSectionPrefix}${s.key}`}
                  className="scroll-mt-32"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                  </div>
                  <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.5rem,2.4vw,2rem)] leading-[1.2] mb-6">
                    {s.title}
                  </h2>
                  {s.body.map((para, idx) => (
                    <p
                      key={idx}
                      className="text-[var(--kr-text-muted)] text-[15.5px] leading-[1.9] mb-5 max-w-[68ch]"
                    >
                      {para}
                    </p>
                  ))}
                </section>
              ))}

              <div className="kr-rule" />
              <p className="text-[12.5px] text-[var(--kr-text-muted)] leading-[1.7] italic max-w-[60ch]">
                Last updated Q1 2026. This page will be revised as vehicles
                are documented and as regulatory authorisations are obtained.
                The most current version of this notice supersedes prior
                versions.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-[var(--kr-line)] py-20 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 flex flex-wrap gap-6 items-center justify-between">
            <h3 className="font-serif-display text-[var(--kr-text)] text-[1.55rem] leading-[1.2] max-w-[44ch]">
              For regulator or counsel correspondence, write to the{" "}
              <span className="italic text-[var(--kr-gold)]">partnership</span>
              .
            </h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="kr-btn-primary">
                Contact Counsel
                <span aria-hidden>→</span>
              </Link>
              <Link to="/legal" className="kr-btn-ghost">
                Legal Notices
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default DisclosuresPage;
